import { WorkflowTaskSummary, WorkflowTaskStatus, WorkflowCaseIndex, LandingReviewItem } from '../../../domain/types/workflow';
import {
  extractDateFromUtcString, getStageName, getDisplayTaskStatus,
  getWorkflowDisplayName, formatYear,
} from '../workflow/workflow-utils';

export interface TaskSummaryLine {
  key: string;
  text: string;
  routerLink: string[] | null;
}

export const EMPTY_TASKS_MESSAGE = 'You have no active or upcoming tasks.';

// Status phrase for the candidate's own-case line, keyed by the server-derived
// statusSummary. "With candidate" is intentionally absent — that state is already
// covered by the candidate-submission task line. "Stalled" is an admin concern.
const CASE_STATUS_PHRASE: Record<string, string> = {
  'In review': 'is in review',
  'In appeal': 'is under appeal',
  'Complete': 'is complete',
};

/**
 * One summary line for the candidate's current own-case, when its status warrants
 * surfacing (in review / in appeal / complete). Names the case type and review
 * period, e.g. "Your Deferral case for 2025-2026 is complete." Only the most
 * recent case is shown so historical completed cases don't pile up — older cases
 * stay in the My Cases tab.
 */
export function buildCaseSummaryLines(cases: WorkflowCaseIndex[]): TaskSummaryLine[] {
  if (!cases.length) return [];
  const current = [...cases].sort((a, b) => b.periodOfReviewId - a.periodOfReviewId)[0];
  const phrase = CASE_STATUS_PHRASE[current.statusSummary];
  if (!phrase) return [];
  const text = `Your ${getWorkflowDisplayName(current.workflowType)} case for ${formatYear(current.periodOfReviewId)} ${phrase}.`;
  return [{ key: `case-${current.caseId}`, text, routerLink: ['/review/packet', String(current.caseId)] }];
}

type Bucket = 'active' | 'pastDue' | 'upcoming';

// Candidate-facing first, then reviewer stages. Stages not listed sort last.
const STAGE_ORDER = [
  'CandidateSubmission', 'CandidateOverview', 'Supervisors', 'LetterOfEvaluation',
  'AdHocCommittee', 'PeerReviewCommittee', 'CandidateRebuttal', 'FinalReviewer',
];
const BUCKET_ORDER: Record<Bucket, number> = { active: 0, pastDue: 1, upcoming: 2 };

// Reuse the shared gate state machine so the landing page can't drift from the
// my-tasks list/detail views if gate semantics change.
function bucketOf(t: WorkflowTaskSummary): Bucket {
  switch (getDisplayTaskStatus(t)) {
    case 'Upcoming': return 'upcoming';
    case 'Past Due': return 'pastDue';
    default: return 'active';
  }
}

function isIncomplete(t: WorkflowTaskSummary): boolean {
  return t.status === WorkflowTaskStatus.Active;
}

export function formatLongDate(dateString: string | null | undefined): string | null {
  const d = extractDateFromUtcString(dateString);
  if (!d) return null;
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export function countActiveTasks(tasks: WorkflowTaskSummary[]): number {
  return tasks.filter(t => isIncomplete(t) && bucketOf(t) !== 'upcoming').length;
}

interface Group {
  stage: string;
  bucket: Bucket;
  openDate?: string | null;
  dueDate?: string | null;
  taskIds: number[];
}

export function buildTaskSummaryLines(tasks: WorkflowTaskSummary[]): TaskSummaryLine[] {
  const groups = new Map<string, Group>();
  for (const t of tasks.filter(isIncomplete)) {
    const bucket = bucketOf(t);
    // Open date is only displayed for the upcoming bucket, so only fold it into
    // the grouping key there — otherwise two active/past-due tasks with the same
    // due date but different open dates would render as duplicate identical lines.
    const openKey = bucket === 'upcoming' ? (t.gateOpenDate ?? '') : '';
    const key = `${t.stage}|${bucket}|${openKey}|${t.dueDate ?? ''}`;
    const g = groups.get(key);
    if (g) {
      g.taskIds.push(t.id);
    } else {
      groups.set(key, { stage: t.stage, bucket, openDate: t.gateOpenDate, dueDate: t.dueDate, taskIds: [t.id] });
    }
  }

  const stageRank = (s: string) => {
    const i = STAGE_ORDER.indexOf(s);
    return i === -1 ? STAGE_ORDER.length : i;
  };

  return Array.from(groups.entries())
    .map(([key, g]) => ({ key, ...g }))
    .sort((a, b) =>
      (stageRank(a.stage) - stageRank(b.stage)) ||
      (BUCKET_ORDER[a.bucket] - BUCKET_ORDER[b.bucket]) ||
      ((a.dueDate ?? '').localeCompare(b.dueDate ?? '')))
    .map(g => buildLine(g));
}

function buildLine(g: Group & { key: string }): TaskSummaryLine {
  const n = g.taskIds.length;
  const stageName = getStageName(g.stage);
  const noun = n === 1 ? 'task' : 'tasks';
  const opensVerb = n === 1 ? 'opens' : 'open';
  const due = formatLongDate(g.dueDate);
  const open = formatLongDate(g.openDate);
  // "an active Supervisors task" (n=1) vs "4 active Supervisors tasks" (n>1).
  const subject = (adjective: string, article: string) =>
    `${n === 1 ? article : n} ${adjective} ${stageName} ${noun}`;

  let text: string;
  if (g.bucket === 'active') {
    text = `You have ${subject('active', 'an')}${due ? ` due ${due}` : ''}.`;
  } else if (g.bucket === 'pastDue') {
    text = `You have ${subject('past-due', 'a')}${due ? ` due ${due}` : ''}.`;
  } else {
    text = `You have ${subject('upcoming', 'an')}`
      + (open ? ` that ${opensVerb} ${open}.` : '.')
      + (due ? ` Due ${due}.` : '');
  }

  return { key: g.key, text, routerLink: linkFor(g) };
}

function linkFor(g: Group): string[] | null {
  if (g.bucket === 'upcoming') return null;
  if (g.stage === 'CandidateSubmission') return ['/review/task', String(g.taskIds[0])];
  return ['/review/my-tasks'];
}

interface ReadOnlyGroup {
  stage: string;
  upcoming: boolean;
  openDate?: string | null;
  dueDate?: string | null;
  count: number;
}

/**
 * Summary lines for read-only reviewer roles (e.g. secondary PRC reviewer), which
 * have no workflow.Task. Grouped by stage and due date, with upcoming roles split
 * out by their open date. Active/past-due roles link to the reviewer dashboard;
 * upcoming ones (not yet open) don't link. Past-due collapses into the available
 * group — a read-only role has nothing to submit by a deadline.
 */
export function buildReadOnlyReviewLines(items: LandingReviewItem[]): TaskSummaryLine[] {
  const groups = new Map<string, ReadOnlyGroup>();
  for (const it of items) {
    const upcoming = it.bucket === 'upcoming';
    const key = `${it.stage}|${upcoming}|${upcoming ? (it.openDate ?? '') : ''}|${it.dueDate ?? ''}`;
    const g = groups.get(key);
    if (g) {
      g.count++;
    } else {
      groups.set(key, { stage: it.stage, upcoming, openDate: it.openDate, dueDate: it.dueDate, count: 1 });
    }
  }

  return Array.from(groups.values())
    .sort((a, b) =>
      (a.upcoming === b.upcoming ? 0 : a.upcoming ? 1 : -1) ||
      getStageName(a.stage).localeCompare(getStageName(b.stage)) ||
      ((a.dueDate ?? '').localeCompare(b.dueDate ?? '')))
    .map(g => {
      const stageName = getStageName(g.stage);
      const noun = g.count === 1 ? 'review' : 'reviews';
      const opensVerb = g.count === 1 ? 'opens' : 'open';
      const due = formatLongDate(g.dueDate);
      if (g.upcoming) {
        const open = formatLongDate(g.openDate);
        const lead = g.count === 1 ? 'an' : g.count;
        return {
          key: `ro-upcoming-${g.stage}-${g.openDate ?? ''}-${g.dueDate ?? ''}`,
          text: `You have ${lead} upcoming read-only ${stageName} ${noun}`
            + (open ? ` that ${opensVerb} ${open}.` : '.')
            + (due ? ` Due ${due}.` : ''),
          routerLink: null,
        };
      }
      const lead = g.count === 1 ? 'a' : g.count;
      return {
        key: `ro-${g.stage}-${g.dueDate ?? ''}`,
        text: `You have ${lead} read-only ${stageName} ${noun}${due ? ` due ${due}` : ''}.`,
        routerLink: ['/review/my-review'],
      };
    });
}
